import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useState, useRef } from "react";
import { Upload, FileText, MapPin, Calendar, Clock, Car } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserVehicles } from "@/hooks/useUserVehicles";
import PhotoUpload from "@/components/PhotoUpload";
import { ROUTES } from "@/lib/routes";

interface PhotoFile {
  file: File;
  preview: string;
  id: string;
}

const UploadRun = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [runData, setRunData] = useState({
    title: '',
    equipmentType: '',
    description: '',
    isPublic: true,
    vehicleId: ''
  });
  
  // Fetch user's vehicles
  const { vehicles } = useUserVehicles(user?.id || '');

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-foreground mb-4">Upload Your Run</h1>
            <p className="text-muted-foreground mb-8">
              Please sign in to upload your GPX tracks.
            </p>
            <Button onClick={() => navigate('/auth')}>
              Sign In
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.gpx')) {
        setSelectedFile(file);
        // Set default title from filename (remove extension and underscores)
        const defaultTitle = file.name.replace('.gpx', '').replace(/_/g, ' ');
        setRunData(prev => ({...prev, title: defaultTitle}));
      } else {
        toast({
          title: "Invalid file type",
          description: "Please select a GPX file.",
          variant: "destructive",
        });
      }
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.name.toLowerCase().endsWith('.gpx')) {
      setSelectedFile(file);
      // Set default title from filename (remove extension and underscores)
      const defaultTitle = file.name.replace('.gpx', '').replace(/_/g, ' ');
      setRunData(prev => ({...prev, title: defaultTitle}));
    } else {
      toast({
        title: "Invalid file type",
        description: "Please select a GPX file.",
        variant: "destructive",
      });
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleUpload = async () => {
    if (!selectedFile || !runData.equipmentType || !runData.title) {
      toast({
        title: "Missing information",
        description: "Please select a file, enter a title, and select equipment type.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      // Upload GPX file to Supabase Storage
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('gpx-files')
        .upload(fileName, selectedFile);

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('gpx-files')
        .getPublicUrl(fileName);

      // Upload photos to storage if any
      let photoUrls: string[] = [];
      if (photos.length > 0) {
        const photoUploadPromises = photos.map(async (photo) => {
          const photoExt = photo.file.name.split('.').pop();
          const photoFileName = `${user.id}/${Date.now()}-${photo.id}.${photoExt}`;
          
          const { data: photoUploadData, error: photoUploadError } = await supabase.storage
            .from('run-photos')
            .upload(photoFileName, photo.file);

          if (photoUploadError) {
            throw photoUploadError;
          }

          const { data: { publicUrl: photoPublicUrl } } = supabase.storage
            .from('run-photos')
            .getPublicUrl(photoFileName);

          return photoPublicUrl;
        });

        photoUrls = await Promise.all(photoUploadPromises);
      }

      // Insert run record without geometry - will be computed from run_tracks
      const { data: runRecord, error: runError } = await supabase
        .from('runs')
        .insert({
          user_id: user.id,
          equipment_type: runData.equipmentType as any,
          source: 'gpx_upload',
          gpx_file_url: publicUrl,
          photo_urls: photoUrls,
          processing_status: 'pending',
          vehicle_id: runData.vehicleId || null,
        })
        .select()
        .single();

      if (runError) {
        console.error('Run insert failed:', { error: runError, equipmentType: runData.equipmentType });
        throw runError;
      }

      // Trigger GPX processing in the background
      const { error: processError } = await supabase.functions.invoke('process-gpx', {
        body: {
          runId: runRecord.id,
          gpxFileUrl: publicUrl,
        },
      });

      if (processError) {
        console.error('GPX processing initiation failed:', processError);
        // Don't fail the upload, just log the error
        toast({
          title: "Upload successful!",
          description: "Your GPX track has been uploaded. Processing may be delayed.",
        });
      } else {
        toast({
          title: "Upload successful!",
          description: "Your GPX track has been uploaded and is being processed.",
        });
      }

      navigate(ROUTES.run(runRecord.id));
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload GPX file.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-foreground mb-2">Upload Your Run</h1>
          <p className="text-muted-foreground mb-8">Share your off-road adventure with the community</p>
          
          <div className="space-y-6">
            {/* GPX File Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  GPX File Upload
                </CardTitle>
                <CardDescription>
                  Upload your GPS track file to share your trail experience
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".gpx"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {selectedFile ? (
                    <div className="space-y-2">
                      <FileText className="h-12 w-12 text-green-500 mx-auto" />
                      <p className="text-lg font-medium text-foreground">{selectedFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Upload className="h-12 w-12 text-muted-foreground mx-auto" />
                      <div>
                        <p className="text-lg font-medium text-foreground mb-2">
                          Drag & drop your GPX file here
                        </p>
                        <p className="text-muted-foreground mb-4">or</p>
                        <Button variant="outline">Choose File</Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Supported formats: GPX files up to 10MB
                  </p>
                  <Link to={ROUTES.gpxTracksGuide} className="text-sm text-primary hover:underline">
                    How to record a GPX track?
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Photo Upload */}
            <PhotoUpload 
              photos={photos}
              onPhotosChange={setPhotos}
              maxPhotos={10}
            />

            {/* Run Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Run Details
                </CardTitle>
                <CardDescription>
                  Provide additional information about your run
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Run Title *</Label>
                  <Input
                    id="title"
                    placeholder="Enter a title for your run"
                    value={runData.title}
                    onChange={(e) => setRunData(prev => ({...prev, title: e.target.value}))}
                  />
                </div>

                 <div className="space-y-2">
                  <Label htmlFor="equipment">Vehicle/Equipment Type *</Label>
                  <Select 
                    value={runData.equipmentType} 
                    onValueChange={(value) => setRunData(prev => ({...prev, equipmentType: value}))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your vehicle type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="suv">SUV / Jeep</SelectItem>
                      <SelectItem value="truck">Truck</SelectItem>
                      <SelectItem value="sxs">Side-by-Side (SxS)</SelectItem>
                      <SelectItem value="atv">ATV</SelectItem>
                      <SelectItem value="dirt_bike">Dirt Bike / Motorcycle</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vehicle">Vehicle from Garage (Optional)</Label>
                  <Select 
                    value={runData.vehicleId || "none"} 
                    onValueChange={(value) => setRunData(prev => ({...prev, vehicleId: value === "none" ? "" : value}))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a vehicle from your garage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {vehicles?.map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.year} {vehicle.make} {vehicle.model}
                          {vehicle.trim && ` ${vehicle.trim}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Link this run to a vehicle in your garage
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your experience, conditions, notable features..."
                    value={runData.description}
                    onChange={(e) => setRunData(prev => ({...prev, description: e.target.value}))}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Upload Button */}
            <div className="flex gap-4">
              <Button 
                onClick={handleUpload} 
                disabled={isUploading || !selectedFile || !runData.equipmentType || !runData.title}
                className="flex-1"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Run
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => navigate('/explore')}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UploadRun;